import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountMenu from './AccountMenu';
import AdminNav from './admin/AdminNav';
import CartDrawer from './CartDrawer';
import LanguageSelect from './LanguageSelect';
import NotificationBell from './NotificationBell';
import refreshCurrentUser from '../utils/refreshCurrentUser';
import api from '../utils/api';
import { clearSession } from '../utils/session';
import { closeOtherMenus, onCloseMenus } from '../utils/menuEvents';

// Mobile menu item states; the sm: overrides keep desktop links unchanged
const MENU_ITEM_STATES = 'hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 sm:focus-visible:ring-0';

export default function Header() {
  const user = JSON.parse(localStorage.getItem('user'));
  const { t } = useTranslation();
  const location = useLocation();
  const [, setTick] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  // A click anywhere outside the header closes the mobile menu
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);
  useEffect(() => { if (menuOpen) closeOtherMenus('header'); }, [menuOpen]);
  useEffect(() => onCloseMenus('header', () => setMenuOpen(false)), []);

  // Revalidate the role when the app regains focus; re-render when it changes
  useEffect(() => {
    const onFocus = () => refreshCurrentUser();
    const onUpdate = () => setTick((t) => t + 1);
    window.addEventListener('focus', onFocus);
    window.addEventListener('user-updated', onUpdate);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('user-updated', onUpdate);
    };
  }, []);

  // Cart item count (total quantity), refreshed when the cart page reports a change
  const [cartCount, setCartCount] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    const load = () => api.get('/api/cart')
      .then((res) => setCartCount((res.data?.items || []).reduce((n, i) => n + i.quantity, 0)))
      .catch(() => {});
    load();
    const onPaid = (e) => setCartCount((n) => Math.max(0, n - (e.detail?.count || 0)));
    window.addEventListener('cart-changed', load);
    window.addEventListener('cart-paid', onPaid);
    return () => {
      window.removeEventListener('cart-changed', load);
      window.removeEventListener('cart-paid', onPaid);
    };
  }, [user]);
  const [cartOpen, setCartOpen] = useState(false);
  // Lets other pages open the same cart drawer as the Cart button
  useEffect(() => {
    const openCart = () => setCartOpen(true);
    window.addEventListener('open-cart', openCart);
    return () => window.removeEventListener('open-cart', openCart);
  }, []);
  const cartLabel = cartCount > 0 ? t('nav.cartCount', { count: cartCount }) : t('nav.cart');

  const logout = () => {
    api.post('/api/users/logout').catch(() => {}).finally(() => {
      clearSession();
      window.location.href = '/login';
    });
  };

  // Same links for desktop (inline) and mobile (dropdown)
  // Logged-out visitors get Login, Register and Help; logged-in users reach Log out from the profile dropdown
  const links = !user ? (
    <>
      <Link to="/login" state={{ from: location.pathname + location.search }} className={`hover:text-green-700 ${MENU_ITEM_STATES}`}><i className="fas fa-sign-in-alt mr-2 sm:hidden" aria-hidden="true" />{t('nav.login')}</Link>
      <Link to="/register" className={`hover:text-green-700 ${MENU_ITEM_STATES}`}><i className="fas fa-user-plus mr-2 sm:hidden" aria-hidden="true" />{t('nav.register')}</Link>
      <Link to="/help" className={`hover:text-green-700 ${MENU_ITEM_STATES}`}><i className="fas fa-question-circle mr-2 sm:hidden" aria-hidden="true" />{t('nav.help')}</Link>
    </>
  ) : null;

  return (
    <header ref={headerRef} className="sticky top-0 z-50 bg-gray-800 text-white px-4 py-2 sm:p-4 flex justify-between items-center gap-2 sm:gap-0">
      <Link to="/home" onClick={() => setMenuOpen(false)} className="flex items-center shrink-0 hover:opacity-80">
        <img src="/logo-header.png" alt="Qercha" className="h-8 sm:h-12 w-auto" />
      </Link>
      <nav className="hidden sm:flex space-x-4 items-center min-w-0">
        {user && <NotificationBell />}
        {user && <button type="button" onClick={() => setCartOpen(true)} className="hover:text-green-700"><i className="fas fa-shopping-cart mr-1 align-middle" aria-hidden="true" />{cartLabel}</button>}
        {user && <AccountMenu user={user} onLogout={logout} />}
        <LanguageSelect />
        {links}
      </nav>
      <div className="flex items-center gap-1.5 sm:hidden min-w-0 whitespace-nowrap">
        {user && <div className="min-w-0"><AccountMenu user={user} onLogout={logout} /></div>}
        {user && <div onClick={() => setMenuOpen(false)}><NotificationBell /></div>}
        {user && <button type="button" onClick={() => { setCartOpen(true); setMenuOpen(false); }} className="hover:text-green-700 text-xs whitespace-nowrap min-h-[44px] inline-flex items-center"><i className="fas fa-shopping-cart mr-1 align-middle" aria-hidden="true" />{cartLabel}</button>}
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={menuOpen}
          className="p-2 text-2xl leading-none min-w-[44px] min-h-[44px]"
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>
      {menuOpen && (
        <div
          onClick={() => setMenuOpen(false)}
          className="sm:hidden absolute top-full left-0 right-0 bg-gray-800 border-t border-gray-700 flex flex-col items-stretch shadow-lg [&>*]:flex [&>*]:items-center [&>*]:justify-start [&>*]:w-full [&>*]:min-h-12 [&>*]:py-3 [&>*]:px-5 [&>*]:text-left"
        >
          {links}
          {/* Collapsible Administration section; stop clicks so toggling it does not close the menu */}
          {user && <div onClick={(e) => e.stopPropagation()}><AdminNav inMenu onNavigate={() => setMenuOpen(false)} /></div>}
          {/* Stop clicks here so picking a language does not close the menu */}
          <div onClick={(e) => e.stopPropagation()}><LanguageSelect /></div>
          {/* Logout reuses the profile menu handler; the menu closes on click */}
          {user && <hr className="border-gray-700" />}
          {user && <button type="button" onClick={logout} className={MENU_ITEM_STATES}><i className="fas fa-sign-out-alt mr-2" aria-hidden="true" />{t('profile.logout')}</button>}
        </div>
      )}
          <CartDrawer open={cartOpen} onClose={() => { setCartOpen(false); window.dispatchEvent(new Event('cart-changed')); }} />
    </header>
  );
}
